import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { BasicSlider } from '../BasicSlider/BasicSlider'

/**
 * Use the Carousel component to create a slideshow that a user can interact with.
 * This slideshow can contain a series of {@link CarouselImage}.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-carousel--carousel.html
 *
 * @param {(boolean|number)} [autoPlay=false] - Automatically go to the next slide, either use `boolean` or `number`, default interval is '5000ms'.
 * @param {boolean} [buttonsOnHover] - Always hide the control buttons and show them only on hover.
 * @param {boolean} [buttonsVisible] - Always show the control buttons, independent of brand and viewport.
 * @param {CarouselImage[]} [children] - A collection of {@link CarouselImage} components.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [counterEnforced] - Enforce a counter as pagination instead of automatic switch from dots to counter.
 * @param {boolean} [fullscreen=false] - Add control button for fullscreen mode.
 * @param {string} [itemAspectRatio] - Set an intrinsic aspect ratio for {@link CarouselImage}, e.g. '16:9'.
 * @param {(boolean|number)} [lazyLoad=false] - Lazy load {@link CarouselImage} inside the Carousel.
 * @param {boolean} [objectFitContain] - Prevent {@link CarouselImage} from being cropped.
 * @param {boolean} [wrapAround=false] - Enables {@link autoPlay} not to stop when the last element is reached.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The Carousel component.
 * @constructor
 */
export function Carousel({
  autoPlay = false,
  buttonsOnHover,
  buttonsVisible,
  children,
  className,
  counterEnforced,
  fullscreen = false,
  itemAspectRatio,
  lazyLoad = false,
  objectFitContain,
  wrapAround = false,
  testId,
  ...otherProps
}) {
  const defaultAutoPlayInterval = 5000
  const initialResizeThreshold = 300

  const [flickityRef, setFlickityRef] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(null)

  /**
   * Handles {@link BasicSlider} controls.
   * @TODO: Should be extracted into a generic FlickityExtension.
   * @see https://jira.platform.vwfs.io/browse/BRON-8286
   */
  const toggleSliderControls = useCallback(() => {
    if (flickityRef) {
      const size = flickityRef.size.width
      const { slideableWidth } = flickityRef
      const slideable = slideableWidth > size

      /**
       * This fixes a bug where Flickity is unable to reassign the draggable
       * behavior when resizing from a big to small viewport when `groupCells`
       * or `contain` options are set. The fix only kicks in when the `draggable`
       * option in Flickity was set.
       * @see https://flickity.metafizzy.co/options.html#draggable
       * @see https://github.com/metafizzy/flickity/issues/960
       */
      if (flickityRef.options.draggable) {
        flickityRef.isDraggable = slideable
        flickityRef.element.classList.toggle('is-draggable', slideable)
        flickityRef.element.classList.toggle('is-slideable', slideable)
      }
    }
  }, [flickityRef])

  /**
   * Handles {@link BasicSlider} side-effects.
   * @TODO: Should be extracted into a generic FlickityExtension.
   * @see https://jira.platform.vwfs.io/browse/BRON-8286
   */
  useEffect(() => {
    const instance = flickityRef
    if (instance) {
      const resizeFn = instance.resize.bind(instance)

      instance.resize = () => {
        instance.element.classList.remove('flickity-resize')
        resizeFn()
        requestAnimationFrame(() => instance.element.classList.add('flickity-resize'))
      }

      const onReady = () => {
        /**
         * Fixes nasty layout bug for some journeys.
         * The {@link initialResizeThreshold} must be increased for this to work.
         * @see https://jira.platform.vwfs.io/browse/BRON-7534
         */
        const timeout = setTimeout(() => {
          resizeFn()
        }, initialResizeThreshold + 50)
        return () => {
          timeout && clearTimeout(timeout)
        }
      }

      instance.on('select', toggleSliderControls)
      instance.on('ready', onReady)

      return () => {
        instance.off('select', toggleSliderControls)
        instance.off('ready', onReady)
      }
    }
    return () => {}
  }, [flickityRef, toggleSliderControls])

  /**
   * Handles {@link BasicSlider} when children change dynamically.
   * @TODO: Should be extracted into a generic FlickityExtension.
   * @see https://jira.platform.vwfs.io/browse/BRON-8286
   */
  useEffect(() => {
    if (flickityRef) {
      /**
       * Resize Flickity and recalculate the position of all children.
       * @see https://flickity.metafizzy.co/api.html#reloadcells
       * @see https://flickity.metafizzy.co/api.html#resize
       */
      flickityRef.reloadCells()
      flickityRef.resize()
    }
  }, [flickityRef, children])

  /**
   * Handles {@link isFullscreen} when entering or existing fullscreen mode.
   */
  useEffect(() => {
    if (flickityRef) {
      flickityRef.on('fullscreenChange', setIsFullscreen)
    }
  }, [flickityRef])

  /**
   * Add `[data-slider-counter]` attribute when {@link BasicSlider} is mounted
   * and {@link counterEnforced} is set.
   */
  useEffect(() => {
    if (flickityRef && counterEnforced) {
      flickityRef.element.setAttribute('data-slider-counter', counterEnforced)
    }
  }, [flickityRef, counterEnforced])

  /**
   * Add `[data-testid]` attribute when {@link BasicSlider} is mounted
   * and {@link testId} is set.
   */
  useEffect(() => {
    if (flickityRef && testId) {
      flickityRef.element.setAttribute('data-testid', testId)
    }
  }, [flickityRef, testId])

  const flickityOptions = {
    autoPlay: autoPlay === true ? defaultAutoPlayInterval : autoPlay,
    cellSelector: '.c-carousel__item',
    fullscreen,
    imagesLoaded: true,
    lazyLoad,
    wrapAround,
  }

  const classNameList = classNames(
    'c-carousel',
    'js-carousel',
    {
      'c-carousel--buttons-visible': buttonsVisible,
      'c-carousel--buttons-on-hover': buttonsOnHover,
    },
    className
  ).trim()

  return (
    <>
      <BasicSlider
        className={classNameList}
        flickityRef={(c) => setFlickityRef(c)}
        options={flickityOptions}
        {...otherProps}
      >
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            /**
             * Set {@link imgSizes} to `100vw` when entering fullscreen mode.
             * @see https://jira.platform.vwfs.io/browse/BRON-6972
             */
            imgSizes: isFullscreen ? '100vw' : child.props.imgSizes,
            itemAspectRatio,
            lazyLoad,
            objectFitContain,
          })
        )}
      </BasicSlider>
    </>
  )
}

Carousel.propTypes = {
  autoPlay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]), // Bronson template: 'data-carousel-auto-play'.
  buttonsOnHover: PropTypes.bool, // Bronson template: 'carousel-modifier'.
  buttonsVisible: PropTypes.bool, // Bronson template: 'carousel-modifier'.
  children: PropTypes.node, // Bronson template: 'carousel-images'.
  className: PropTypes.string, // Additional CSS classes on the component element.
  counterEnforced: PropTypes.bool, // Bronson template: 'data-slider-counter'.
  fullscreen: PropTypes.bool, // Bronson template: 'fullscreen'.
  itemAspectRatio: PropTypes.string, // Bronson template: 'itemAspectRatio'.
  lazyLoad: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]), // Bronson template: 'lazyLoad'.
  objectFitContain: PropTypes.bool, // Bronson template: 'carousel-image-classes'.
  wrapAround: PropTypes.bool, // Bronson template: 'data-carousel-wrap-around'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * The CarouselImage component is used internally to wrap the images
 * and display them within the {@link Carousel}.
 *
 * Note: {@link itemAspectRatio}, {@link lazyLoad} and {@link objectFitContain}
 * are set by parent {@link Carousel}.
 *
 * @param {string} [altText] - The `[alt]` attribute on the image element.
 * @param {string} [className] - Additional CSS classes on the image element.
 * @param {string} [imgSizes] - The `[sizes]` attribute on the image element.
 * @param {string} [imgSrc] - The `[src]` attribute on the image element.
 * @param {string} [imgSrcSet] - The `[srcset]` attribute on the image element.
 * @param {Carousel.itemAspectRatio} [itemAspectRatio] - {@link Carousel.itemAspectRatio}.
 * @param {Carousel.lazyLoad} [lazyLoad] - {@link Carousel.lazyLoad}.
 * @param {Carousel.objectFitContain} [objectFitContain] - {@link Carousel.objectFitContain}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The CarouselImage component.
 * @constructor
 */
function CarouselImage({
  altText,
  className,
  imgSizes,
  imgSrc,
  imgSrcSet,
  itemAspectRatio,
  lazyLoad,
  objectFitContain,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-carousel__item', {
    'o-ratio': itemAspectRatio,
    [`o-ratio--${itemAspectRatio}`]: itemAspectRatio,
  }).trim()

  const imgClassNameList = classNames(
    'c-carousel__image',
    {
      'u-object-fit-contain': objectFitContain,
      'o-ratio__content': itemAspectRatio,
    },
    className
  ).trim()

  /**
   * Handles the use of specific attributes for Flickity or
   * native HTML attributes depending on {@link lazyLoad}.
   */
  const imgProps = {
    [`${lazyLoad ? 'data-flickity-lazyload-src' : 'src'}`]: imgSrc,
    [`${lazyLoad ? 'data-flickity-lazyload-srcset' : 'srcSet'}`]: imgSrcSet,
  }

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      <img className={imgClassNameList} sizes={imgSizes} alt={altText} {...imgProps} />
    </div>
  )
}

CarouselImage.propTypes = {
  altText: PropTypes.string, // Bronson template: 'altText'.
  className: PropTypes.string, // Additional CSS classes on the image element.
  imgSizes: PropTypes.string, // Bronson template: 'slotSizes'.
  imgSrc: PropTypes.string, // Bronson template: 'src'.
  imgSrcSet: PropTypes.string, // Bronson template: 'srcset'.
  itemAspectRatio: PropTypes.string, // Bronson template: 'itemAspectRatio'.
  lazyLoad: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]), // Bronson template: 'lazyLoad'.
  objectFitContain: PropTypes.bool, // Bronson template: 'carousel-image-classes'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

CarouselImage.displayName = 'Carousel.Image'
Carousel.Image = CarouselImage
